import { createContext, useCallback, useContext, useEffect, useRef } from "react";

const UpdateCtx = createContext(null);

const POLL_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;

function currentBuildId() {
  return document.querySelector('meta[name="build-id"]')?.content || "";
}

// Verdadero si el usuario no está escribiendo en ningún campo de texto
function noInputFocused() {
  const el = document.activeElement;
  if (!el) return true;
  const t = el.tagName?.toUpperCase();
  return t !== "INPUT" && t !== "TEXTAREA" && t !== "SELECT" && !el.isContentEditable;
}

export function UpdateProvider({ children }) {
  const lastCheck = useRef(0);
  const knownBuild = useRef(currentBuildId());
  const reloadPending = useRef(false);

  const tryReload = useCallback(() => {
    if (!reloadPending.current) return;
    if (noInputFocused()) {
      window.location.reload();
    }
    // Si hay un input activo, no hacer nada — se reintentará en el próximo
    // evento de visibilitychange / focus o en el siguiente poll.
  }, []);

  const check = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheck.current < MIN_GAP_MS) return;
    lastCheck.current = now;
    if (!knownBuild.current) return;
    try {
      const res = await fetch(`/version.json?t=${now}`, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.build && data.build !== knownBuild.current) {
        reloadPending.current = true;
        // Dar 3 segundos de gracia y recargar si el usuario no está en un campo
        setTimeout(tryReload, 3_000);
      }
    } catch {
      // Sin red o archivo inexistente (dev): ignorar
    }
  }, [tryReload]);

  useEffect(() => {
    check();
    const interval = setInterval(check, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        check();
        tryReload(); // al volver al tab, intentar recargar si hay versión pendiente
      }
    };
    const onFocus = () => {
      check();
      tryReload();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [check, tryReload]);

  return <UpdateCtx.Provider value={null}>{children}</UpdateCtx.Provider>;
}

export const useUpdateCheck = () => useContext(UpdateCtx);
